// ============================================================================
// CR071: MERCHANT LOGO RESOLVER - Single entry point for all logo resolution
// Resolution order: DB inline SVG → DB URL → Legacy local file → Fallback
// ============================================================================

import { getMerchant } from "./merchantIndex";
import { getMerchantLogoPath, getPurchaseBrandLogoPath } from "./logoRegistry";

// Types
export type LogoResultType = "inline" | "url" | "local" | "none";

export interface LogoResult {
  type: LogoResultType;
  src?: string;      // For url/local types
  svg?: string;      // For inline type
  alt: string;       // Always present
  merchantKey?: string;
}

/**
 * Normalize merchant name to key format
 * e.g., "Walmart Canada" → "walmart-canada"
 */
export function normalizeMerchantKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Resolve merchant logo with fallback chain
 * 
 * Resolution order:
 * 1. DB inline SVG (logo_svg)
 * 2. DB URL (logo_url) 
 * 3. Legacy logoRegistry.ts local file
 * 4. Fallback (none - will use initial)
 */
export function resolveMerchantLogo(merchantName: string | null | undefined): LogoResult {
  if (!merchantName) {
    return { type: "none", alt: "Unknown" };
  }

  const merchantKey = normalizeMerchantKey(merchantName);
  const alt = merchantName;

  // 1. Try database cache (merchant index)
  const merchant = getMerchant(merchantKey);
  if (merchant) {
    // 1a. Inline SVG
    if (merchant.logo_svg) {
      return {
        type: "inline",
        svg: merchant.logo_svg,
        alt,
        merchantKey,
      };
    }

    // 1b. Logo URL
    if (merchant.logo_url) {
      return {
        type: "url",
        src: merchant.logo_url,
        alt,
        merchantKey,
      };
    }
  }

  // 2. Try legacy local file (logoRegistry.ts)
  const localPath = getMerchantLogoPath(merchantName);
  if (localPath) {
    return {
      type: "local",
      src: localPath,
      alt,
      merchantKey,
    };
  }

  // 3. Fallback - no logo found
  return {
    type: "none",
    alt,
    merchantKey,
  };
}

/**
 * Resolve purchase brand logo (gift card brand)
 * 
 * Resolution order:
 * 1. Legacy logoRegistry.ts local file (purchase brands)
 * 2. Fallback (none)
 * 
 * Note: Purchase brands don't have DB entries yet - can be extended later
 */
export function resolvePurchaseBrandLogo(brandName: string | null | undefined): LogoResult {
  if (!brandName) {
    return { type: "none", alt: "Unknown" };
  }

  const alt = brandName;

  // Try legacy local file
  const localPath = getPurchaseBrandLogoPath(brandName);
  if (localPath) {
    return {
      type: "local",
      src: localPath,
      alt,
    };
  }

  // Fallback
  return {
    type: "none",
    alt,
  };
}

/**
 * Resolve any brand logo (tries merchant first, then purchase)
 */
export function resolveBrandLogo(name: string | null | undefined): LogoResult {
  if (!name) {
    return { type: "none", alt: "Unknown" };
  }

  // Try merchant first
  const merchantResult = resolveMerchantLogo(name);
  if (merchantResult.type !== "none") {
    return merchantResult;
  }

  // Then try purchase brand
  return resolvePurchaseBrandLogo(name);
}

/**
 * Get fallback initial for logo placeholder
 */
export function getLogoFallbackInitial(name: string | null | undefined): string {
  if (!name) return "?";
  return name.charAt(0).toUpperCase();
}

/**
 * Helper: Check if a logo result has a valid logo
 */
export function hasValidLogo(result: LogoResult): boolean {
  return result.type !== "none";
}
